import { useEffect, useMemo, useState } from "react";
import Toggle from "../../components/ui/Toggle";
import Icon from "../../components/ui/Icon";
import DimmerSlider from "../../components/ui/DimmerSlider";
import { BRAND } from "../../brand";
import { CHANNEL_COUNT } from "../../constants/app";
import SlideOverlay from "../../components/overlays/SlideOverlay";
import DialogModal from "../../components/overlays/DialogModal";

const accent = BRAND.magenta;
const resetOptions = ["Reset", "Last Value", "Defined Value"];
const modeOptions = ["Dimming", "Switching", "Direct"];
const reactionOptions = ["Alarm", "Turn Off"];
const defaultChannelLabels = Array.from({ length: CHANNEL_COUNT }, (_, i) => `Ch ${i + 1}`);

export default function DeviceConfigOverlay({
  isOpen,
  device,
  onClose,
  onSave,
  mappingBuses = [],
  mappingPreset = null,
  mappingNodes = [],
  onDelete,
}) {
  const initialForm = useMemo(() => {
    if (!device) return null;
    const buses =
      mappingNodes.length > 0
        ? mappingNodes.map((n) => ({ id: n.id, label: n.name || n.id }))
        : mappingBuses.length > 0
        ? mappingBuses.map((label) => ({ id: label, label }))
        : [{ id: "ewl-810-1", label: "EWL-810 #1" }];
    const emptyMappings = buses.reduce((acc, bus) => ({ ...acc, [bus.label]: {} }), {});
    const assignedMappings = {};
    const inUse = mappingPreset?.__inUse || {};
    Object.entries(inUse).forEach(([key, owner]) => {
      if (owner !== device.id) return;
      const [bus, chLabel] = key.split("::");
      assignedMappings[bus] = { ...(assignedMappings[bus] || {}), [chLabel]: true };
    });
    const mergedMappings =
      Object.keys(assignedMappings).length > 0 ? assignedMappings : emptyMappings;
    return {
      name: device.name || "",
      showOnDashboard: Boolean(device.showOnDashboard),
      mode: device.mode || (device.dimmable ? "Dimming" : "Switching"),
      turnOnDelay: device.turnOnDelay ?? 0,
      turnOffDelay: device.turnOffDelay ?? 0,
      resetBehavior: device.resetBehavior || "Defined Value",
      definedValue: device.definedValue ?? device.control?.dim ?? 50,
      presetOnValue: device.control?.presetOnValue ?? 50,
      currentLimit: device.currentLimit ?? "5.0",
      overcurrentDuration: device.overcurrentDuration ?? "500",
      reaction: device.reaction || "Alarm",
      softFuseEnabled: device.softFuseEnabled ?? true,
      mappings: mergedMappings,
      mappingBuses: buses,
      mappingInUse: inUse,
    };
  }, [device, mappingBuses, mappingPreset, mappingNodes]);

  const [form, setForm] = useState(initialForm);
  const [baseForm, setBaseForm] = useState(initialForm);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmMapping, setConfirmMapping] = useState(null);
  const [releaseQueue, setReleaseQueue] = useState([]);

  useEffect(() => {
    setForm(initialForm);
    setBaseForm(initialForm);
    setReleaseQueue([]);
  }, [initialForm]);

  if (!device || !form) return null;

  const isDirty = JSON.stringify(form) !== JSON.stringify(baseForm);

  const toggleMapping = (bus, ch) => {
    const key = `${bus}::${ch}`;
    const mappedElsewhere = mappingPreset?.__inUse?.[key];
    if (mappedElsewhere && mappedElsewhere !== device.id) {
      setConfirmMapping({ bus, ch, current: mappedElsewhere });
      return;
    }
    setForm((prev) => ({
      ...prev,
      mappings: {
        ...prev.mappings,
        [bus]: { ...(prev.mappings?.[bus] || {}), [ch]: !prev.mappings?.[bus]?.[ch] },
      },
    }));
  };

  const handleSave = () => {
    const releaseKeys = [...releaseQueue];
    Object.entries(form.mappings || {}).forEach(([bus, chans]) => {
      Object.entries(chans).forEach(([ch, active]) => {
        if (active) {
          const key = `${bus}::${ch}`;
          const currentOwner = form.mappingInUse[key];
          if (currentOwner && currentOwner !== device.id) releaseKeys.push(key);
        }
      });
    });

    const desiredMappings = [];
    Object.entries(form.mappings || {}).forEach(([bus, chans]) => {
      Object.entries(chans).forEach(([ch, active]) => {
        if (active) desiredMappings.push(`${bus}::${ch}`);
      });
    });

    onSave({
      name: form.name,
      showOnDashboard: form.showOnDashboard,
      mode: form.mode,
      turnOnDelay: form.turnOnDelay,
      turnOffDelay: form.turnOffDelay,
      resetBehavior: form.resetBehavior,
      definedValue: form.definedValue,
      presetOnValue: form.presetOnValue,
      currentLimit: form.currentLimit,
      overcurrentDuration: form.overcurrentDuration,
      reaction: form.reaction,
      softFuseEnabled: form.softFuseEnabled,
      mappings: {},
      dimmable: form.mode === "Dimming",
      control: { ...(device.control || {}), dim: form.definedValue, presetOnValue: form.presetOnValue },
      releaseMappings: releaseKeys,
      desiredMappings,
    });
    setBaseForm(form);
    setReleaseQueue([]);
  };

  const showDefinedSlider = form.mode === "Dimming" && form.resetBehavior === "Defined Value";
  const resetOptionsForMode =
    form.mode === "Switching" ? ["Reset", "Last Value", "Turn On Value"] : resetOptions;

  return (
    <SlideOverlay isOpen={isOpen} className="bg-gradient-to-b from-brand-purple to-brand-navy">
      <div className="h-full overflow-y-auto px-4 pb-4">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 -mx-4 px-4 pb-2 pt-3 bg-brand-navy/95 backdrop-blur-sm flex items-center gap-3">
          <button
            type="button"
            onClick={() => (isDirty ? setShowUnsaved(true) : onClose())}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-purple text-brand-light hover:bg-brand-purple/80 transition"
            aria-label="Back"
          >
            <Icon name="arrow_back" className="text-xl" />
          </button>
          <div className="flex-1 min-w-0">
            <span className="text-lg font-black italic text-brand-light truncate block">
              Configure {device.name}
            </span>
          </div>
          {onDelete ? (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-red-900/40 text-red-200 hover:bg-red-800/60 transition"
              aria-label="Delete"
            >
              <Icon name="delete" className="text-lg" />
            </button>
          ) : (
            <div className="h-10 w-10" />
          )}
        </div>

        <div className="space-y-4 pt-2">
          {/* Name */}
          <Section>
            <Label text="Name" />
            <Input
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </Section>

          {/* Show on Dashboard */}
          <Section className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-purple text-brand-light">
                <Icon name="dashboard" className="text-xl" />
              </span>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-brand-light">Show on Dashboard</span>
                <span className="text-[11px] text-brand-light/50">Surface this device in quick controls</span>
              </div>
            </div>
            <Toggle
              on={Boolean(form.showOnDashboard)}
              onChange={(next) => setForm((prev) => ({ ...prev, showOnDashboard: next }))}
              suppressPropagation
            />
          </Section>

          {/* Mode */}
          <Section>
            <Label text="Mode" />
            <div className="grid grid-cols-3 gap-2">
              {modeOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      mode: opt,
                      resetBehavior:
                        opt === "Direct" && prev.resetBehavior !== "Reset"
                          ? "Reset"
                          : prev.resetBehavior,
                    }))
                  }
                  className={`rounded-lg px-3 py-2 text-sm font-semibold ${
                    form.mode === opt ? "bg-brand-magenta text-white" : "bg-brand-purple text-brand-light"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
            {(form.mode === "Switching" || form.mode === "Direct") && (
              <div className="mt-3 flex items-center gap-3">
                <Label text="Turn On Value" />
                <DimmerSlider
                  value={form.presetOnValue ?? 50}
                  onChange={(val) => setForm((prev) => ({ ...prev, presetOnValue: val }))}
                  className="flex-1"
                  knobColor="white"
                  trackColor={accent}
                />
                <span className="text-sm font-semibold text-brand-magenta">{form.presetOnValue ?? 50}%</span>
              </div>
            )}
          </Section>

          {/* Delays */}
          <Section>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label text="Turn On Delay (s)" />
                <Input
                  type="number"
                  step="1"
                  value={form.turnOnDelay}
                  onChange={(e) => setForm((prev) => ({ ...prev, turnOnDelay: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label text="Turn Off Delay (s)" />
                <Input
                  type="number"
                  step="1"
                  value={form.turnOffDelay}
                  onChange={(e) => setForm((prev) => ({ ...prev, turnOffDelay: e.target.value }))}
                />
              </div>
            </div>
          </Section>

          {/* Reset Behaviour */}
          {form.mode !== "Direct" && (
            <Section>
              <Label text="Reset Behaviour" />
              <div className="grid grid-cols-3 gap-2">
                {resetOptionsForMode.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, resetBehavior: opt }))}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold ${
                      form.resetBehavior === opt
                        ? "bg-brand-magenta text-white"
                        : "bg-brand-purple text-brand-light"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {showDefinedSlider && (
                <div className="mt-3 flex items-center gap-3">
                  <Label text="Defined Value" />
                  <DimmerSlider
                    value={form.definedValue}
                    onChange={(val) => setForm((prev) => ({ ...prev, definedValue: val }))}
                    className="flex-1"
                    knobColor="white"
                    trackColor={accent}
                  />
                  <span className="text-sm font-semibold text-brand-magenta">{form.definedValue}%</span>
                </div>
              )}
            </Section>
          )}

          {/* Soft Fuse */}
          <Section>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-brand-light">Soft Fuse</span>
              <Toggle
                on={Boolean(form.softFuseEnabled)}
                onChange={(next) => setForm((prev) => ({ ...prev, softFuseEnabled: next }))}
                suppressPropagation
              />
            </div>
            {form.softFuseEnabled !== false && (
              <div className="space-y-3 pt-1">
                <div className="space-y-1">
                  <Label text="Current Limit (A)" />
                  <Input
                    type="number"
                    value={form.currentLimit}
                    onChange={(e) => setForm((prev) => ({ ...prev, currentLimit: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label text="Overcurrent Duration (ms)" />
                  <Input
                    type="number"
                    value={form.overcurrentDuration}
                    onChange={(e) => setForm((prev) => ({ ...prev, overcurrentDuration: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label text="Reaction" />
                  <div className="grid grid-cols-2 gap-2">
                    {reactionOptions.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, reaction: opt }))}
                        className={`rounded-lg px-3 py-2 text-sm font-semibold ${
                          form.reaction === opt
                            ? "bg-brand-magenta text-white"
                            : "bg-brand-purple text-brand-light"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Section>

          {/* Mapping */}
          <Section>
            <span className="text-sm font-semibold text-brand-light">Mapping</span>
            <div className="space-y-3 pt-1">
              {(form.mappingBuses || []).map((bus) => {
                const label = typeof bus === "string" ? bus : bus.label || bus.id;
                const inUseCount = Object.entries(mappingPreset?.__inUse || {}).filter(
                  ([key, owner]) => key.startsWith(`${label}::`) && owner !== device.id
                ).length;
                return (
                  <div key={label} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-brand-light/50">{label}</span>
                      {inUseCount > 0 && (
                        <span className="text-[11px] text-amber-300">
                          {inUseCount} channel(s) already mapped
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {defaultChannelLabels.map((ch) => {
                        const active = form.mappings?.[label]?.[ch];
                        const key = `${label}::${ch}`;
                        const mappedElsewhere = mappingPreset?.__inUse?.[key];
                        const isFree = !mappedElsewhere || mappedElsewhere === device.id;
                        return (
                          <button
                            key={`${label}-${ch}`}
                            type="button"
                            onClick={() => toggleMapping(label, ch)}
                            title={mappedElsewhere && mappedElsewhere !== device.id ? `In use by ${mappedElsewhere}` : undefined}
                            className={`rounded-lg px-2 py-1 text-xs font-semibold border ${
                              active
                                ? "bg-brand-magenta text-white border-brand-magenta"
                                : isFree
                                ? "bg-brand-purple text-brand-light border-brand-purple/70"
                                : "bg-[#2a1b14] text-amber-200 border-amber-500/60"
                            }`}
                          >
                            {ch}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        </div>

        {/* Save button */}
        <div className="sticky bottom-0 -mx-4 px-4 pb-3 pt-4 bg-gradient-to-b from-transparent via-brand-navy to-brand-navy">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty}
              className={`flex-1 rounded-2xl px-4 py-3 text-sm font-semibold shadow transition active:scale-[0.99] ${
                isDirty
                  ? "bg-brand-magenta text-white hover:bg-brand-pink"
                  : "bg-brand-purple text-brand-light/40 cursor-not-allowed"
              }`}
            >
              Save Changes
            </button>
            {onDelete && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="rounded-2xl border border-red-600/60 bg-red-900/40 px-4 py-3 text-sm font-semibold text-red-100 hover:bg-red-900/60 transition"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      <DialogModal isOpen={showDeleteConfirm} position="absolute">
          <div className="w-full max-w-sm rounded-2xl border border-brand-purple bg-brand-navy p-4 space-y-3 shadow-xl">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500/20 text-red-200">
                <Icon name="warning" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-brand-light">Delete device?</span>
                <span className="text-[11px] text-brand-light/50">
                  This will remove the channel and its mappings.
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => { onDelete(device.id); setShowDeleteConfirm(false); }}
                className="w-full rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 transition"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full rounded-xl border border-brand-purple bg-brand-purple px-4 py-2 text-sm font-semibold text-brand-light hover:border-brand-magenta transition"
              >
                Cancel
              </button>
            </div>
          </div>
      </DialogModal>

      {/* Unsaved changes */}
      <DialogModal isOpen={showUnsaved} position="absolute">
          <div className="w-full max-w-sm rounded-2xl border border-brand-purple bg-brand-navy p-4 space-y-3 shadow-xl">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/20 text-amber-300">
                <Icon name="warning" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-brand-light">Unsaved Changes</span>
                <span className="text-[11px] text-brand-light/50">
                  Save or discard before leaving this configuration.
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => { handleSave(); setShowUnsaved(false); onClose(); }}
                className="w-full rounded-xl bg-brand-magenta px-4 py-2 text-sm font-semibold text-white hover:bg-brand-pink transition"
              >
                Save &amp; Close
              </button>
              <button
                type="button"
                onClick={() => { setForm(baseForm); setShowUnsaved(false); onClose(); }}
                className="w-full rounded-xl border border-brand-purple bg-brand-purple px-4 py-2 text-sm font-semibold text-brand-light hover:border-brand-magenta transition"
              >
                Discard Changes
              </button>
              <button
                type="button"
                onClick={() => setShowUnsaved(false)}
                className="w-full rounded-xl px-4 py-2 text-sm font-semibold text-brand-light/70 hover:text-brand-light transition"
              >
                Cancel
              </button>
            </div>
          </div>
      </DialogModal>

      {/* Overwrite mapping confirmation */}
      <DialogModal isOpen={!!confirmMapping} position="absolute">
          <div className="w-full max-w-sm rounded-2xl border border-brand-purple bg-brand-navy p-4 space-y-3 shadow-xl">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/20 text-amber-300">
                <Icon name="warning" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-brand-light">Channel already mapped</span>
                <span className="text-[11px] text-brand-light/50">
                  Release from <strong className="text-brand-light">{confirmMapping?.current}</strong> and assign here?
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  const { bus, ch } = confirmMapping ?? {};
                  const key = `${bus}::${ch}`;
                  setReleaseQueue((prev) => prev.includes(key) ? prev : [...prev, key]);
                  setForm((prev) => {
                    const nextMappings = { ...prev.mappings, [bus]: { ...(prev.mappings?.[bus] || {}), [ch]: true } };
                    const nextInUse = { ...(prev.mappingInUse || {}), [key]: device.id };
                    return { ...prev, mappings: nextMappings, mappingInUse: nextInUse };
                  });
                  setConfirmMapping(null);
                }}
                className="w-full rounded-xl bg-brand-magenta px-4 py-2 text-sm font-semibold text-white hover:bg-brand-pink transition"
              >
                Release &amp; Assign
              </button>
              <button
                type="button"
                onClick={() => setConfirmMapping(null)}
                className="w-full rounded-xl border border-brand-purple bg-brand-purple px-4 py-2 text-sm font-semibold text-brand-light hover:border-brand-magenta transition"
              >
                Cancel
              </button>
            </div>
          </div>
      </DialogModal>
    </SlideOverlay>
  );
}

function Section({ children, className = "" }) {
  return (
    <div className={`rounded-xl border border-brand-purple bg-brand-purple p-3 space-y-2 ${className}`}>
      {children}
    </div>
  );
}

function Label({ text }) {
  return <span className="text-xs font-semibold text-brand-light/70">{text}</span>;
}

function Input({ className = "", ...rest }) {
  return (
    <input
      {...rest}
      className={`w-full rounded-lg border border-brand-purple bg-brand-navy px-3 py-2 text-sm text-brand-light placeholder:text-brand-light/40 focus:outline-none focus:ring-1 focus:ring-brand-magenta ${className}`}
    />
  );
}
